// @flow

import React, { useState } from 'react';
import styled from '@emotion/styled';
import { connect } from 'react-redux';
import { get } from 'lodash/fp';
import MobileDetect from 'mobile-detect';

import {
  createPlayUpAccount,
  getCampaignStatus,
  getCurrentUser,
  resetPlayUpCampaign
} from 'app/actions/userActions';
import { playUpDepositCampaign } from 'app/api/user';
import Modal from 'app/components/commons/Modal';
import Checkbox from 'app/components/commons/Checkbox';
import Button from 'app/components/commons/Button';
import InputField from 'app/components/commons/InputField';
import SelectField from 'app/components/form/SelectField';
import FormValidationMessage from 'app/components/commons/FormValidationMessage';
import Background from 'app/images/campaigns/playup/background.jpg';
import BackgroundMobile from 'app/images/campaigns/playup/background_mobile.jpg';
import BackgroundAFL from 'app/images/campaigns/playup/background_football.jpg';
import BetWithPlayUp from 'app/images/campaigns/playup/betwith.svg';
import DoubleUp from 'app/images/campaigns/playup/doubleup.svg';
import FreeText from 'app/images/campaigns/playup/free.svg';
import GameOnText from 'app/images/campaigns/playup/gameon.svg';
import WelcomeText from 'app/images/campaigns/playup/welcome.svg';

const actions = {
  CLAIM: 'claim',
  REQUIRES_PASSWORD: 'requires_password',
  ACCOUNT_NOT_CREATED: 'account_not_created',
  ACCOUNT_ALREADY_EXISTS: 'account_already_exists',
  ACCOUNT_CREATED: 'account_created',
  TRY_AGAIN: 'error_creating_account_try_again',
  NOT_TRY_AGAIN: 'error_creating_account_not_try_again',
  DEPOSIT_NOW: 'deposit'
};

const events = {
  CLICKED: 'CLICKED',
  SUCCESS: 'SUCCESS',
  INVALID: 'INVALID',
  FAILURE: 'FAILURE',
  REJECTED: 'REJECTED'
};

const ios = 'https://www.playup.com.au/AppStore';
const android = 'https://www.playup.com.au/AndroidApp';
const playUpUrl = 'https://www.playup.com.au';
const depositUrl = 'https://www.playup.com.au/Account/Banking';

type Action = $Keys<typeof actions>;

type Props = {
  currencySymbol?: string,
  isChecking: boolean,
  action?: Action,
  message?: Object,
  campaignUsername?: string,
  campaignUrl?: string,
  userName: string,
  campaignProcessed: boolean,
  campaignProcessing: boolean,
  onGetStatus: () => void,
  onRefresh: () => void,
  onCancel: () => void,
  onCreate: () => void,
  onReset: () => void
};

const defaultProps = {
  currencySymbol: undefined,
  action: undefined,
  message: undefined,
  campaignUrl: undefined,
  campaignUsername: undefined
};

const HeaderText = styled.div(({ theme }) => ({
  textAlign: 'center',
  fontSize: theme.fontSize.medium,
  fontWeight: theme.fontWeight.demiBold,
  marginBottom: theme.spacing.small
}));

const TitleWrapper = styled.div(({ theme }) => ({
  width: '100%',
  backgroundColor: theme.color.black,
  height: 76,
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center'
}));

const Title = styled.div(({ theme }) => ({
  textAlign: 'center',
  width: '100%',
  backgroundColor: theme.color.black,
  color: theme.color.white,
  fontSize: 28,
  [theme.mediaQuery.mobileOnly]: {
    fontSize: 26
  }
}));

const BoldText = styled.span(({ theme }) => ({
  fontWeight: theme.fontWeight.bold
}));

const durations = [
  {
    value: 1,
    label: '1 Day'
  },
  {
    value: 7,
    label: '7 Days'
  },
  {
    value: 14,
    label: '14 Days'
  },
  {
    value: 30,
    label: '30 Days'
  }
];

const Wrapper = styled.div({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  backgroundColor: 'white'
});

const ButtonContainer = styled.div({
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  marginBottom: 30
});

const TermsLink = styled.span({
  textDecoration: 'underline'
});

const Content = styled.div({
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center'
});

const FormContainer = styled.div({
  display: 'flex',
  alignItems: 'center',
  flexDirection: 'column'
});

const Form = styled.div({
  width: 300,
  display: 'flex',
  justifyContent: 'center',
  flexDirection: 'column'
});

const CancelButton = styled(Button)(({ theme }) => ({
  marginTop: theme.spacing.small
}));

const Header = styled.div({
  position: 'relative',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center'
});

const Logo = styled.img(({ theme }) => ({
  position: 'absolute',
  top: 30,
  left: 30,
  [theme.mediaQuery.mobileOnly]: {
    top: 20,
    left: 20
  }
}));

const LogoMobile = styled.img({});

const Image = styled.img(({ theme }) => ({
  position: 'absolute',
  bottom: 0,
  [theme.mediaQuery.mobileOnly]: {
    width: '85%'
  }
}));

const ImageMobile = styled.img({});

const BackgroundImage = styled.img({
  width: '100%'
});

const PlayUpText = styled.span(({ theme }) => ({
  color: theme.color.playUpGreen || theme.color.green,
  fontSize: theme.fontSize.large
}));

const PlayUpTitle = styled.span(({ theme }) => ({
  color: theme.color.playUpGreen || theme.color.green
}));

const ContentContainer = styled.div({
  position: 'absolute',
  display: 'flex',
  flexDirection: 'column',
  alignItem: 'center',
  justifyContent: 'space-between',
  top: 40,
  bottom: 10
});

const ImageLogoContainer = styled.div({
  display: 'flex',
  flexDirection: 'column',
  alignItem: 'center',
  justifyContent: 'center'
});

const BodyWrapper = styled.div(({ theme }) => ({
  padding: theme.spacing.large,
  textAlign: 'center'
}));

const SmallText = styled.p(({ theme }) => ({
  fontSize: theme.fontSize.small,
  textAlign: 'center',
  lineHeight: '20px'
}));

const fixture = {
  [actions.CLAIM]: {
    Image: FreeText,
    Title: () => (
      <TitleWrapper>
        <Title>DOUBLE UP ON THE EXCITEMENT</Title>
        <Title style={{ marginTop: -10 }}>
          <PlayUpTitle>GET $20 FREE</PlayUpTitle> TO BET AT PLAYUP
        </Title>
      </TitleWrapper>
    ),
    Body: ({ firstname }: { firstname: string }) => (
      <div>
        <p>
          <BoldText>{firstname}</BoldText>, join the 300,000 other legends on our betting app. From
          Australia&apos;s best fixed odds bookie. New Punters only. 18+ Gamble Responsibly.
        </p>
      </div>
    ),
    Header: null
  },
  [actions.REQUIRES_PASSWORD]: {
    Image: WelcomeText,
    Title: () => (
      <TitleWrapper>
        <Title>YOU&apos;RE UP $20! NICE ONE MATE.</Title>
      </TitleWrapper>
    ),
    Body: () => (
      <div>
        Now choose a password. Make it unique.
        <br />
        And try to remember it.
      </div>
    ),
    Header: 'ACCOUNT SET-UP'
  },
  [actions.ACCOUNT_CREATED]: {
    Image: GameOnText,
    Title: () => (
      <TitleWrapper>
        <Title>YOU&apos;RE IN. YOU LEGEND.</Title>
      </TitleWrapper>
    ),
    Body: ({ username }: { username: string }) => (
      <div>
        Log in with your username ( <PlayUpText>{username}</PlayUpText> ) and password. It&apos;s
        time to make life exciting. You ready?
      </div>
    ),
    Header: 'LOG IN'
  },
  [actions.DEPOSIT_NOW]: {
    Image: DoubleUp,
    Title: () => (
      <TitleWrapper>
        <Title>DEPOSIT $100 AND</Title>
        <Title style={{ marginTop: -10 }}>
          <PlayUpTitle>GET $200</PlayUpTitle> TO BET WITH
        </Title>
      </TitleWrapper>
    ),
    Body: () => (
      <div>
        <p>
          We&apos;ve added a <BoldText>DOUBLE UP</BoldText> bonus to your PlayUp account.
          <br />
          Simply login and deposit any amount up to $100 and we will double it.
        </p>
        <div>
          <BoldText>PLUS</BoldText> if you deposit $100 you will also get a{' '}
          <BoldText>FREE TICKET</BoldText> into the <BoldText>MASSIVE</BoldText> AFL $75,000+
          SATURDAY Draftstars contest.
        </div>
      </div>
    ),
    Background: BackgroundAFL,
    Header: null
  },
  default: {
    Image: WelcomeText,
    Title: null,
    Body: null,
    Header: null
  }
};

const HIDE_PLAYUP_CAMPAIGN_KEY = 'hide_playup_campaign';

export const checkRejected = () => {
  try {
    if (window.localStorage.getItem(HIDE_PLAYUP_CAMPAIGN_KEY)) {
      return true;
    }
  } catch (err) {
    // console.log(err);
  }
  return false;
};

const PlayUpCampaignModal = ({
  onCreate,
  onGetStatus,
  onRefresh,
  currencySymbol,
  userName,
  campaignProcessed,
  campaignProcessing,
  isChecking,
  campaignUrl,
  campaignUsername,
  onCancel,
  onReset,
  action,
  message
}: Props) => {
  const [password, setPassword] = useState('');
  const [limit, setLimit] = useState('');
  const [duration, setDuration] = useState('');
  const [showLimit, setShowLimit] = useState(false);
  const [error, setError] = useState(undefined);

  const handleCancel = () => {
    if (campaignProcessed) {
      onRefresh();
    }
    if ([actions.ACCOUNT_CREATED].includes(action)) {
      onReset();
    }
    onCancel();
  };

  const handleClaim = () => {
    onGetStatus();
  };

  const handleToggleDepositLimit = () => setShowLimit(!showLimit);

  const handleCreate = () => {
    const regex = new RegExp(/^(?=.*\d)(?=.*[a-zA-Z])[0-9a-zA-Z]{6,}$/);
    if (regex.test(password) === false) {
      setError(
        `Password minimum 6 characters, must contain at least one number and one alphabetic character, alphanumeric only`
      );
      return;
    }

    if (showLimit) {
      if (limit === '' || parseFloat(limit) < 0) {
        setError('Please enter a valid limit amount');
        return;
      }
      if (!duration) {
        setError('Please choose period');
        return;
      }
    }
    onCreate({
      action,
      not_interested: 0,
      password,
      set_deposit_limit: showLimit ? 1 : 0,
      deposit_limit: showLimit ? parseFloat(limit) : undefined,
      deposit_duration: showLimit ? parseInt(duration, 10) : undefined
    });
  };

  const handlePasswordChange = val => setPassword(val);

  const handleDelete = () => {
    try {
      window.localStorage.setItem(HIDE_PLAYUP_CAMPAIGN_KEY, 'true');
    } catch (err) {
      // console.log(err);
    }
    onCancel();
  };

  const handleRejectDeposit = () => {
    playUpDepositCampaign({ not_interested: 1 });
    handleDelete();
    onRefresh();
  };

  const handleDeposit = () => {
    playUpDepositCampaign({ interested: 1 });
    window.open(depositUrl, '_blank');
    onRefresh();
    onCancel();
  };

  const assets = get(action, fixture) || fixture.default;

  const md = new MobileDetect(window.navigator.userAgent);
  const isMobile = !!md.phone();

  if (!action) {
    return null;
  }

  if (action === actions.ACCOUNT_NOT_CREATED) {
    onRefresh();
    return null;
  }

  const modalUp = isMobile && [actions.CLAIM].includes(action);

  return (
    <Modal
      bare
      isOpen
      animation={modalUp ? 'modalUp' : undefined}
      onCancel={handleCancel}
      lightCloseButton
      noPadding={isMobile}
      shouldCloseOnOverlayClick={false}
      size="small"
      positionStyle={modalUp ? { position: 'absolute', left: 0, right: 0, bottom: 0 } : undefined}
    >
      {modalUp && (
        <Wrapper>
          <Header>
            <BackgroundImage src={BackgroundMobile} alt="Loading..." />
            <ContentContainer>
              {action === actions.CLAIM && (
                <Button
                  block
                  size="claim"
                  kind="playup"
                  disabled={isChecking}
                  onClick={handleClaim}
                  style={{ width: 200 }}
                >
                  Claim My $20
                </Button>
              )}
              <ImageLogoContainer>
                <ImageMobile src={assets.Image} style={{ width: '95%', margin: '0 auto' }} />
                <LogoMobile
                  src={BetWithPlayUp}
                  style={{
                    width: action === 'deposit' ? '70%' : '80%',
                    margin: '0 auto',
                    padding: 5
                  }}
                />
                <Button
                  kind="cancel-playup"
                  onClick={handleDelete}
                  style={{ background: 'transparent' }}
                >
                  No thanks, I don&apos;t like free money.
                </Button>
              </ImageLogoContainer>
            </ContentContainer>
          </Header>
        </Wrapper>
      )}
      {!modalUp && (
        <Wrapper>
          <Header>
            <Logo src={BetWithPlayUp} />
            <BackgroundImage src={assets.Background || Background} alt="Loading..." />
            <Image src={assets.Image} />
          </Header>
          {assets.Title && <assets.Title />}
          {!assets.Title && message.title && (
            <TitleWrapper>
              <Title>{message.title}</Title>
            </TitleWrapper>
          )}
          <BodyWrapper>
            {assets.Header && <HeaderText>{assets.Header}</HeaderText>}
            {!assets.Header && message.header && <HeaderText>{message.header}</HeaderText>}
            {assets.Body && <assets.Body firstname={userName} username={campaignUsername} />}
            {!assets.Body && message.msg && <p>{message.msg}</p>}
          </BodyWrapper>
          <Content>
            {[actions.REQUIRES_PASSWORD, actions.TRY_AGAIN].includes(action) && (
              <FormContainer>
                <Form>
                  <InputField
                    type="password"
                    name="password"
                    placeholder="Password"
                    value={password}
                    onChange={handlePasswordChange}
                    lightContent
                  />
                  <div style={{ marginBottom: 10 }}>
                    <Checkbox
                      checked={showLimit}
                      name="set_limits"
                      uppercase={false}
                      onChange={handleToggleDepositLimit}
                    >
                      I want to set a deposit limits (optional)
                    </Checkbox>
                  </div>
                  {showLimit && (
                    <div>
                      <InputField
                        label="Limit Amount"
                        type="number"
                        name="limit"
                        helper={currencySymbol}
                        uppercase={false}
                        placeholder="AUD"
                        min={0}
                        max={99999999}
                        value={limit}
                        onChange={val => setLimit(val)}
                        lightContent
                      />
                      <SelectField
                        defaultText="CHOOSE PERIOD"
                        label="Limit Period"
                        uppercase={false}
                        lightContent
                        name="duration"
                        options={durations}
                        onChange={val => setDuration(val)}
                        value={duration}
                      />
                    </div>
                  )}
                </Form>
                {error && <FormValidationMessage message={error} />}
                <ButtonContainer>
                  <Button
                    block
                    size="medium"
                    kind="playup"
                    disabled={campaignProcessing}
                    onClick={handleCreate}
                    style={{ width: 227 }}
                  >
                    Create My Account
                  </Button>
                  <CancelButton
                    kind="terms"
                    href="https://www.playup.com.au/TermCond"
                    target="_blank"
                  >
                    <SmallText>
                      By proceeding you accept these standard{' '}
                      <TermsLink>terms and conditions</TermsLink>
                      <br />
                      18+ Gamble Responsibly.
                    </SmallText>
                  </CancelButton>
                </ButtonContainer>
              </FormContainer>
            )}
            {action === actions.ACCOUNT_CREATED && (
              <ButtonContainer>
                <Button
                  block
                  size="medium"
                  kind="playup"
                  disabled={isChecking}
                  href={campaignUrl || playUpUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{ width: 150 }}
                >
                  GO TO WEBSITE
                </Button>
                <Button
                  block
                  size="medium"
                  kind="playup"
                  disabled={isChecking}
                  href={(md.os() || '').toLowerCase().includes('android') ? android : ios}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{ width: 150, marginTop: 20 }}
                >
                  GET THE APP
                </Button>
              </ButtonContainer>
            )}
            {[actions.NOT_TRY_AGAIN, actions.ACCOUNT_ALREADY_EXISTS].includes(action) && (
              <ButtonContainer>
                <Button
                  block
                  size="medium"
                  kind="playup"
                  onClick={handleCancel}
                  style={{ width: 150 }}
                >
                  CLOSE
                </Button>
              </ButtonContainer>
            )}
            {action === actions.DEPOSIT_NOW && (
              <ButtonContainer>
                <Button
                  block
                  size="medium"
                  kind="playup"
                  onClick={handleDeposit}
                  style={{ width: 150 }}
                >
                  DEPOSIT NOW
                </Button>
                <BodyWrapper>
                  No catches. No gimmicks. Just a great deal to give PlayUp a try.
                  <br />
                  <BoldText>Offer expires 11:59pm Friday, so be quick!</BoldText>
                </BodyWrapper>
                <Button kind="cancel-playup" onClick={handleRejectDeposit}>
                  <SmallText>No thanks, I don&apos;t like free money.</SmallText>
                </Button>
              </ButtonContainer>
            )}
            {action === actions.CLAIM && (
              <ButtonContainer>
                <Button
                  block
                  size="medium"
                  kind="playup"
                  disabled={isChecking}
                  onClick={handleClaim}
                  style={{ width: 150 }}
                >
                  CLAIM MY $20
                </Button>
                <Button kind="cancel-playup" onClick={handleDelete} style={{ marginTop: 10 }}>
                  <SmallText>No thanks, I don&apos;t like free money.</SmallText>
                </Button>
              </ButtonContainer>
            )}
          </Content>
        </Wrapper>
      )}
    </Modal>
  );
};

PlayUpCampaignModal.defaultProps = defaultProps;

const mapStateToProps = state => ({
  isChecking: get('user.campaignIsChecking', state),
  campaignUsername: get('user.campaignUsername', state),
  campaignUrl: get('user.campaignUrl', state),
  currencySymbol: get('settings.currency.symbol', state) || '$',
  userName: get('user.currentUser.first_name', state),
  campaignProcessed: get('user.campaignProcessed', state),
  campaignProcessing: get('user.campaignProcessing', state),
  action: get('user.campaignAction', state) || get('user.currentUser.campaignAction', state),
  message: get('user.campaignMessage', state)
});

const dispatchActions = {
  onCreate: createPlayUpAccount,
  onGetStatus: getCampaignStatus,
  onRefresh: getCurrentUser,
  onReset: resetPlayUpCampaign
};

const connector = connect(mapStateToProps, dispatchActions);

export default connector(PlayUpCampaignModal);
